import { useCallback } from 'react';
import { ClerkProvider } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useClerkAppearance } from '../../auth/clerkAppearance';
import {
  CLERK_SIGN_IN_URL,
  CLERK_SIGN_UP_URL,
  CLERK_AFTER_SIGN_IN_URL,
} from '../../auth/clerkConfig';
import { logClerkRouterNav, normalizeClerkAuthPath } from '../../auth/authLifecycleDebug';

/**
 * Single ClerkProvider with theme-aware appearance on the provider (not per SignIn/SignUp).
 * Must render inside BrowserRouter — routerPush/routerReplace keep Clerk steps on the same
 * React Router route so SignUp is not remounted and verification OTP stays valid.
 */
export function ClerkProviderWithAppearance({ publishableKey, children }) {
  const navigate = useNavigate();
  const appearance = useClerkAppearance();

  const routerPush = useCallback(
    (to) => {
      const normalized = normalizeClerkAuthPath(to);
      logClerkRouterNav('push', to, normalized);
      navigate(normalized);
    },
    [navigate]
  );

  const routerReplace = useCallback(
    (to) => {
      const normalized = normalizeClerkAuthPath(to);
      logClerkRouterNav('replace', to, normalized);
      navigate(normalized, { replace: true });
    },
    [navigate]
  );

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      routerPush={routerPush}
      routerReplace={routerReplace}
      signInUrl={CLERK_SIGN_IN_URL}
      signUpUrl={CLERK_SIGN_UP_URL}
      signInFallbackRedirectUrl={CLERK_AFTER_SIGN_IN_URL}
      signUpFallbackRedirectUrl={CLERK_AFTER_SIGN_IN_URL}
      afterSignOutUrl={CLERK_SIGN_IN_URL}
      appearance={appearance}
    >
      {children}
    </ClerkProvider>
  );
}
